import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // On mount, check if we have a valid session (cookie-based or localStorage fallback)
  useEffect(() => {
    const stored = sessionStorage.getItem('pd_user');
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch { /* ignore */ }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    const { user: userData } = res.data;
    setUser(userData);
    sessionStorage.setItem('pd_user', JSON.stringify(userData));
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore logout errors */ }
    setUser(null);
    sessionStorage.removeItem('pd_user');
    navigate('/login');
  }, [navigate]);

  const axiosAuth = useCallback(() => {
    const instance = axios.create({
      baseURL: `${API}/api`,
      withCredentials: true,
    });
    instance.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401) {
          setUser(null);
          sessionStorage.removeItem('pd_user');
          navigate('/login');
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, axiosAuth, token: null }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
